import {
  EProjectType,
  ESdChangeReason,
  IFillStyle,
  IKuikaStyle,
  IStylingEffects,
  InvisibleStylePart,
  StyleState,
  Visibility
} from "@kuika/kuika-cl-model";
import { FormInstance } from "antd/es/form";
import React from "react";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaPopover from "../../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import { styleSectionNeedsRender } from "../styling-window-helpers";
import { applyEffectPreset, applyEffectPresetFill } from "./effects-presets-apply";
import EffectsPresetsBrowser from "./effects-presets-browser";
import type { EffectPreset } from "./effects-presets-types";

// Microtask flush delay between styling-window's state-mode switch and our
// commit. handleStateChange does setState → render → form.resetFields →
// setFieldsValue → submit; handleStyleFormFinish is async and dispatches via
// applyPropertyGridChangesToDisplay. A small macrotask delay lets that chain
// settle before our setFieldsValue + submit lands, so the second submit is
// the last writer and saveFormDataToStateStyles routes preset values into
// the right state bucket.
const STATE_SWITCH_FLUSH_MS: number = 50;

export interface IEffectsPresetsComponentProps {
  form: FormInstance<IKuikaStyle>;
  /**
   * Callback wired to StylingWindow.handleStateChange. For state-targeted
   * presets (hover/pressed/focused/disabled) the panel switches into that
   * state first; the commit then writes preset values to base form paths,
   * and saveFormDataToStateStyles lifts them into the matching state bucket
   * during submit. If omitted, state-targeted presets fall back to whatever
   * state the panel currently sits in.
   */
  onRequestStateChange?: (state: StyleState) => void;
  /**
   * Wired to StylingWindow.markStatePresetCommit. Called right before a
   * state-targeted preset is submitted so the styling-window routes it into the
   * state bucket ONLY (no base-preview write). Without it the preset leaks onto
   * the base/none state and renders at rest as well as in the target state.
   */
  onPrepareStatePresetCommit?: () => void;
}

interface IEffectsPresetsComponentState {
  open: boolean;
}

class EffectsPresetsComponent extends PureSdComponent<
  IEffectsPresetsComponentProps & IWithSDProps,
  IEffectsPresetsComponentState
> {
  constructor(props: IEffectsPresetsComponentProps & IWithSDProps) {
    super(props);
    this.state = { open: false };
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => styleSectionNeedsRender(changeReason);

  private isEffectsHidden = (): boolean =>
    this.props.sd.getStylesVisibility(InvisibleStylePart.Effects) === Visibility.Hidden;

  /**
   * Whether a `hover` state renders on this screen's target, which decides whether the browser offers the
   * hover-targeted presets at all.
   *
   * <p>False on a mobile screen of a mobile project: a touch screen has no hover, so neither React Native
   * nor the native mobile generators have a state for it and the styling panel projects none. Keyed on the
   * screen rather than the generation type for exactly that reason — unlike Shadow and Effects themselves,
   * this is not a "which generator" question; no mobile generator has hover.</p>
   */
  private hoverApplies = (): boolean =>
    this.props.sd.project.projectType === EProjectType.Desktop || this.props.sd.getIsWebScreen();

  private handleVisibilityChange = (next: boolean): void => {
    this.setState({ open: next });
  };

  private commitPreset = (preset: EffectPreset): void => {
    const kuikaid: string | undefined = this.props.sd.selectedKuikaIds?.[0];
    if (!kuikaid) return;

    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    const currentEffects: IStylingEffects | undefined = fieldsValue.effects;
    const nextEffects: IStylingEffects = applyEffectPreset(currentEffects, preset, { kuikaid });

    // Glass presets also need a translucent background so their backdrop-filter
    // blur is visible — an opaque named fill would hide it. applyEffectPresetFill
    // returns undefined for every non-glass preset, leaving the fill untouched.
    // Glass presets are base-targeted, so this base-path fill write persists to
    // the base fill via handleStyleFormFinish's "none" branch.
    const nextFill: IFillStyle | undefined = applyEffectPresetFill(fieldsValue.fill, preset, kuikaid);
    if (nextFill) {
      fieldsValue.fill = nextFill;
    }

    // Same persistence contract as styling-effects-transitions-component:
    // setFieldsValue + submit() persists the change via styling-window's
    // handleStyleFormFinish. Never call resetFields() — it unmounts every
    // Form.Item subtree and kills popovers/state (KOA-17421).
    //
    // When selectedState is "hover"/"pressed"/"focused"/"disabled",
    // saveFormDataToStateStyles routes the base-path fields we write here
    // into the matching state bucket. That's why this writes to base paths
    // even for state-targeted presets — the caller switches state mode
    // FIRST via onRequestStateChange.
    fieldsValue.effects = nextEffects;
    this.props.form.setFieldsValue(fieldsValue);
    // Flag the upcoming submit so the styling-window writes a state-targeted
    // preset into the state bucket only (no base-preview mirror) — otherwise
    // the effect also lands on the base/none state.
    if (preset.targetState !== "base") {
      this.props.onPrepareStatePresetCommit?.();
    }
    this.props.form.submit();
  };

  private handleApply = (preset: EffectPreset): void => {
    if (preset.targetState !== "base") {
      if (this.props.onRequestStateChange) {
        // State-mode switch first; styling-window's handleStateChange reloads
        // the form with the bucket's existing values and dispatches its own
        // submit. We defer the preset commit so it lands AFTER that async
        // chain settles — otherwise the state-change submit could overwrite
        // our preset values.
        this.props.onRequestStateChange(preset.targetState);
        window.setTimeout((): void => {
          this.commitPreset(preset);
        }, STATE_SWITCH_FLUSH_MS);
      } else {
        // eslint-disable-next-line no-console
        console.warn(
          `[EffectsPresetsComponent] preset "${preset.id}" targets state "${preset.targetState}" but onRequestStateChange is not wired — applying to whatever state the panel currently sits in.`
        );
        this.commitPreset(preset);
      }
    } else {
      this.commitPreset(preset);
    }

    this.setState({ open: false });
  };

  render(): React.ReactNode {
    if (this.isEffectsHidden()) {
      return null;
    }

    return (
      <div className="effects-section__presets">
        <KuikaPopover
          visible={this.state.open}
          onVisibleChange={this.handleVisibilityChange}
          position="left"
          align="center"
          sideOffset={8}
          hideCloseIcon
          className="effects-presets-popover-content"
          title="Effect Presets"
          content={<EffectsPresetsBrowser onApply={this.handleApply} hoverApplies={this.hoverApplies()} />}
        >
          <KuikaButton
            type="secondary"
            color="neutral"
            size="sm"
            className="effects-section__presets-trigger"
            data-testid="effects-presets-trigger"
          >
            <KuikaSvg
              name="sparkles"
              type="datasource-icons"
              width={14}
              height={14}
              className="effects-section__presets-trigger-spark"
            />
            <KuikaSmallText style={{ color: "var(--dark-7)", fontWeight: 500 }}>Presets</KuikaSmallText>
          </KuikaButton>
        </KuikaPopover>
      </div>
    );
  }
}

export default withSD<IEffectsPresetsComponentProps>(EffectsPresetsComponent);
